import { StrictMode, lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Route, Routes, Link } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import './styles.css';
import './lib/i18n';
import Layout from '@/components/Layout';
import Landing from '@/pages/Landing';
import { PageSpinner } from '@/components/ui';
import { useT } from '@/lib/i18n';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { fetchConfig, keys } from '@/api/hooks';
import { registerWorker } from '@/lib/push';

const OrderPage = lazy(() => import('@/flow/OrderPage'));
const Login = lazy(() => import('@/pages/Login'));
const Me = lazy(() => import('@/pages/Me'));
const Gallery = lazy(() => import('@/pages/Gallery'));
const Create = lazy(() => import('@/pages/Create'));
const TemplatePage = lazy(() => import('@/pages/TemplatePage'));
const Studio = lazy(() => import('@/studio/Studio'));
const StudioEditor = lazy(() => import('@/studio/Editor'));
const AdminApp = lazy(() => import('@/admin/AdminApp'));
const JobPage = lazy(() => import('@/pages/Job'));
const WalletPage = lazy(() => import('@/pages/Wallet'));
const Discover = lazy(() => import('@/pages/Discover'));

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 5_000 } },
});
// The service worker only handles push notifications; registering it lets taps open the right page.
void registerWorker();
void queryClient.prefetchQuery({ queryKey: keys.config, queryFn: fetchConfig }).then(() => {
  // Spec §13: the denser type and spacing scale is one class on <html>, behind the ui_compact flag.
  const c = queryClient.getQueryData<Awaited<ReturnType<typeof fetchConfig>>>(keys.config);
  document.documentElement.classList.toggle('ui-compact', !!c?.flags?.ui_compact);
});

function NotFound() {
  const { t } = useT();
  return (
    <section className="py-10">
      <h1 className="text-2xl font-bold">{t('err.order_not_found')}</h1>
      <Link className="btn btn-primary mt-4" to="/">{t('nav.home')}</Link>
    </section>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Suspense fallback={<PageSpinner />}>
            <Routes>
              <Route path="/admin/*" element={<AdminApp />} />
              <Route element={<Layout />}>
                <Route index element={<Discover />} />
                <Route path="about" element={<Landing />} />
                <Route path="o/:token" element={<OrderPage />} />
                <Route path="login" element={<Login />} />
                <Route path="me" element={<Me />} />
                <Route path="create" element={<Create />} />
                <Route path="templates" element={<Gallery />} />
                <Route path="t/:id" element={<TemplatePage />} />
                <Route path="j/:id" element={<JobPage />} />
                <Route path="wallet" element={<WalletPage />} />
                <Route path="studio" element={<Studio />} />
                <Route path="studio/:id" element={<StudioEditor />} />
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster position="bottom-center" richColors closeButton />
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
